import React, { useState } from 'react';
import styled from 'styled-components';
import yaml from 'js-yaml';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 100px;
  z-index: 2000;
`;

const Modal = styled.div`
  background: white;
  border-radius: 8px;
  width: 600px;
  max-width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 16px 20px;
  border: none;
  border-bottom: 1px solid #e1e4e8;
  font-size: 16px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-bottom-color: #0969da;
  }
`;

const Results = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const ResultItem = styled.div`
  padding: 12px 20px;
  border-bottom: 1px solid #e1e4e8;
  cursor: pointer;
  transition: background 0.15s;
  
  &:hover {
    background: #f6f8fa;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const ResultPath = styled.div`
  font-size: 13px;
  color: #0969da;
  font-weight: 600;
  margin-bottom: 4px;
`;

const ResultText = styled.div`
  font-size: 12px;
  color: #24292f;
`;

const ResultLine = styled.span`
  font-size: 11px;
  color: #586069;
  margin-left: 8px;
`;

const NoResults = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #586069;
  font-size: 14px;
`;

function SearchModal({ spec, format, initialQuery = '', onClose, onJumpToLine }) {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  
  const getPathContext = (lines, lineIndex) => {
    let context = [];
    for (let i = lineIndex; i >= 0; i--) {
      const line = lines[i].trim();
      if (line.startsWith('paths:')) break;
      if (line.match(/^\/[^:]*:/)) {
        context.unshift(line.replace(':', ''));
      } else if (line.match(/^(get|post|put|delete|patch):/)) {
        context.unshift(line.replace(':', '').toUpperCase());
      }
    }
    return context.join(' > ') || 'Root';
  };

  const handleSearch = React.useCallback((searchQuery) => {
    setQuery(searchQuery);
    
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    const lines = spec.split('\n');
    const found = [];
    const lowerQuery = searchQuery.toLowerCase();

    lines.forEach((line, index) => {
      if (line.toLowerCase().includes(lowerQuery)) {
        found.push({
          line: index + 1,
          text: line.trim(),
          path: getPathContext(lines, index)
        });
      }
    });

    setResults(found);
  }, [spec]);

  React.useEffect(() => {
    if (initialQuery) {
      handleSearch(initialQuery);
    }
  }, [initialQuery, handleSearch]);

  const handleResultClick = (result) => {
    if (format === 'yaml' && onJumpToLine) {
      onJumpToLine(result.line);
      onClose();
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <SearchInput
          type="text"
          placeholder="Search in spec... (paths, methods, schemas, etc.)"
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          autoFocus
        />
        <Results>
          {query && results.length === 0 && (
            <NoResults>No results found for "{query}"</NoResults>
          )}
          {results.map((result, index) => (
            <ResultItem key={index} onClick={() => handleResultClick(result)}>
              <ResultPath>
                {result.path}
                <ResultLine>Line {result.line}</ResultLine>
              </ResultPath>
              <ResultText>{result.text}</ResultText>
            </ResultItem>
          ))}
        </Results>
      </Modal>
    </Overlay>
  );
}

export default SearchModal;
